import { useState } from 'react';
import { useFactors, useFactorStats, useUpdateFactor, useCreateFactor } from '../hooks/useFactors';
import { useTags, useCreateTag, useDeleteTag, useToggleFactorTag } from '../hooks/useTags';
import Badge from '../components/ui/Badge';
import StatCard from '../components/ui/StatCard';
import Modal from '../components/ui/Modal';

const CATEGORIES = {
  headlines_ctas: 'Headlines & CTAs',
  trust_social_proof: 'Trust & Social Proof',
  page_speed_ux: 'Page Speed & UX',
  form_optimization: 'Form Optimierung',
  mobile_experience: 'Mobile Experience',
  content_relevance: 'Content Relevanz',
  navigation: 'Navigation',
  checkout_flow: 'Checkout Flow',
};

const IMPACTS = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
const IMPACT_COLORS = {
  high: { bg: '#FEE2E2', color: '#EF4444' },
  medium: { bg: '#FEF3C7', color: '#F59E0B' },
  low: { bg: '#D1FAE5', color: '#10B981' },
};

export default function Factors() {
  const [tab, setTab] = useState('factors');
  const [category, setCategory] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [impact, setImpact] = useState('');
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showNewFactor, setShowNewFactor] = useState(false);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newFactor, setNewFactor] = useState({ factor_id: '', name: '', category: 'headlines_ctas', impact: 'medium', check_type: 'ai', prompt_hint: '', bias: '' });
  const [newTag, setNewTag] = useState({ name: '', description: '', color: '#0C9488' });

  const { data: factors = [], isLoading } = useFactors({ category, page_type: tagFilter, impact, search });
  const { data: stats } = useFactorStats();
  const { data: tags = [] } = useTags();
  const updateFactor = useUpdateFactor();
  const createFactor = useCreateFactor();
  const createTag = useCreateTag();
  const deleteTag = useDeleteTag();
  const toggleFactorTag = useToggleFactorTag();

  const startEdit = (f) => {
    setEditId(f.id);
    setEditData({ name: f.name, prompt_hint: f.prompt_hint, bias: f.bias || '', impact: f.impact });
  };

  const saveEdit = async () => {
    await updateFactor.mutateAsync({ id: editId, ...editData });
    setEditId(null);
  };

  const toggleEnabled = (f) => updateFactor.mutate({ id: f.id, enabled: !f.enabled });

  const handleCreateFactor = async () => {
    if (!newFactor.factor_id || !newFactor.name) return;
    try {
      await createFactor.mutateAsync(newFactor);
      setShowNewFactor(false);
      setNewFactor({ factor_id: '', name: '', category: 'headlines_ctas', impact: 'medium', check_type: 'ai', prompt_hint: '', bias: '' });
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.name) return;
    try {
      await createTag.mutateAsync(newTag);
      setShowNewTag(false);
      setNewTag({ name: '', description: '', color: '#0C9488' });
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const TABS = [
    { key: 'factors', label: `Faktoren (${factors.length})` },
    { key: 'tags', label: `Tags (${tags.length})` },
  ];

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-[23px] font-bold mb-0.5">CRO-Faktoren & Tags</h1>
          <p className="text-gray-500 text-[13.5px]">Faktoren, Biases und Seitentyp-Tags verwalten</p>
        </div>
        <div className="flex gap-2.5">
          <button onClick={() => setShowNewTag(true)} className="px-4 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50">+ Neuer Tag</button>
          <button onClick={() => setShowNewFactor(true)} className="px-4 py-2.5 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90">+ Neuer Faktor</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex gap-3.5 mb-5 flex-wrap">
          <StatCard label="Gesamt" value={stats.total} sub={`${stats.enabled} aktiv`} icon="◉" iconBg="#E6F4F2" iconColor="#0C9488" />
          <StatCard label="High Impact" value={stats.by_impact?.high || 0} icon="▲" iconBg="#FEE2E2" iconColor="#EF4444" />
          <StatCard label="Tags" value={tags.length} icon="▦" iconBg="#DBEAFE" iconColor="#3B82F6" />
          <StatCard label="Mit Bias" value={stats.with_bias || 0} icon="◎" iconBg="#EDE9FE" iconColor="#8B5CF6" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-bg rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-md text-[13px] transition-colors ${tab === t.key ? 'bg-white shadow-sm font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: FAKTOREN ═══ */}
      {tab === 'factors' && (
        <>
          {/* Filter */}
          <div className="flex gap-2.5 mb-4 flex-wrap">
            <input className="border border-gray-200 rounded-lg px-3.5 py-2 text-[13px] outline-none focus:border-teal w-[220px]" placeholder="Suchen…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal bg-white" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Alle Kategorien</option>
              {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal bg-white" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
              <option value="">Alle Tags</option>
              {tags.map((t) => <option key={t.id} value={t.slug}>{t.name} ({t.factors_count})</option>)}
            </select>
            <select className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal bg-white" value={impact} onChange={(e) => setImpact(e.target.value)}>
              <option value="">Alle Impacts</option>
              {Object.entries(IMPACTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          {/* Tabelle */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 text-left">
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium w-8">An</th>
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium">Name</th>
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium w-[120px]">Kategorie</th>
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium w-[70px]">Impact</th>
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium">Bias</th>
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium">Tags</th>
                  <th className="px-3 py-3 text-[11px] text-gray-500 font-medium w-20 text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {factors.map((f) => {
                  const ic = IMPACT_COLORS[f.impact] || IMPACT_COLORS.medium;
                  if (editId === f.id) {
                    return (
                      <tr key={f.id} className="border-b border-gray-100 bg-teal-light/30">
                        <td className="px-3 py-3" colSpan={7}>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="text-[11px] text-gray-500 block mb-1 font-medium">Name</label>
                              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                            </div>
                            <div>
                              <label className="text-[11px] text-gray-500 block mb-1 font-medium">Bias</label>
                              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" value={editData.bias} onChange={(e) => setEditData({ ...editData, bias: e.target.value })} placeholder="z.B. Mere Exposure Effect, Social Proof" />
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="text-[11px] text-gray-500 block mb-1 font-medium">Prompt Hint</label>
                            <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal h-[60px] resize-none" value={editData.prompt_hint} onChange={(e) => setEditData({ ...editData, prompt_hint: e.target.value })} />
                          </div>
                          {/* Tag-Zuordnung */}
                          <div className="mb-3">
                            <label className="text-[11px] text-gray-500 block mb-1.5 font-medium">Tags zuordnen</label>
                            <div className="flex flex-wrap gap-1.5">
                              {tags.map((t) => {
                                const assigned = (f.tags || []).some(ft => ft.id === t.id);
                                return (
                                  <button
                                    key={t.id}
                                    onClick={() => toggleFactorTag.mutate({ tagId: t.id, factorId: f.id })}
                                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-all ${assigned ? 'text-white font-medium' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
                                    style={assigned ? { backgroundColor: t.color, borderColor: t.color } : {}}
                                  >
                                    {t.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditId(null)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-[12px] hover:bg-gray-50">Abbrechen</button>
                            <button onClick={saveEdit} disabled={updateFactor.isPending} className="px-3 py-1.5 bg-teal text-white rounded-lg text-[12px] font-medium hover:opacity-90 disabled:opacity-50">Speichern</button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={f.id} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors ${!f.enabled ? 'opacity-40' : ''}`}>
                      <td className="px-3 py-2">
                        <button onClick={() => toggleEnabled(f)} className={`w-8 h-[18px] rounded-full transition-colors relative ${f.enabled ? 'bg-teal' : 'bg-gray-300'}`}>
                          <div className={`w-[14px] h-[14px] bg-white rounded-full absolute top-[2px] transition-all ${f.enabled ? 'left-[16px]' : 'left-[2px]'}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-[12.5px] font-medium">{f.name}</div>
                        <div className="text-[10px] font-mono text-gray-400">{f.factor_id}</div>
                      </td>
                      <td className="px-3 py-2"><Badge bg="#F3F4F6" color="#6B7280">{CATEGORIES[f.category] || f.category}</Badge></td>
                      <td className="px-3 py-2"><Badge bg={ic.bg} color={ic.color}>{IMPACTS[f.impact]}</Badge></td>
                      <td className="px-3 py-2 text-[11px] text-purple-600 max-w-[180px] truncate">{f.bias || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {(f.tags || []).map((t) => (
                            <span key={t.id} className="text-[9px] text-white rounded px-1.5 py-0.5" style={{ backgroundColor: t.color }}>{t.name}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => startEdit(f)} className="text-[12px] text-teal font-medium hover:underline">Bearbeiten</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {isLoading && <div className="py-8 text-center text-gray-400 text-sm">Wird geladen…</div>}
            {!isLoading && factors.length === 0 && <div className="py-8 text-center text-gray-400 text-sm">Keine Faktoren gefunden.</div>}
          </div>
        </>
      )}

      {/* ═══ TAB: TAGS ═══ */}
      {tab === 'tags' && (
        <div className="grid grid-cols-[1fr_300px] gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
            <div className="px-5 py-3.5 border-b border-gray-200">
              <h3 className="font-semibold text-[14.5px]">Seitentyp-Tags</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">Tags definieren welche Faktoren bei einer Analyse geprüft werden</p>
            </div>
            {tags.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium">{t.name}</div>
                  <div className="text-[11px] text-gray-500">{t.slug} · {t.factors_count} Faktoren</div>
                  {t.description && <div className="text-[11px] text-gray-400 mt-0.5">{t.description}</div>}
                </div>
                {t.is_system ? (
                  <Badge bg="#F3F4F6" color="#6B7280">System</Badge>
                ) : (
                  <button onClick={() => { if (confirm(`Tag "${t.name}" löschen?`)) deleteTag.mutate(t.id); }} className="text-[12px] text-red-500 hover:text-red-700">Löschen</button>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5 h-fit">
            <h3 className="font-semibold text-[14.5px] mb-3">Info</h3>
            <p className="text-[13px] text-gray-500 leading-relaxed mb-3">
              Tags sind Seitentypen die bei der Analyse ausgewählt werden. Jeder Tag hat eine Menge zugeordneter CRO-Faktoren.
            </p>
            <p className="text-[13px] text-gray-500 leading-relaxed mb-3">
              <strong>System-Tags</strong> (Homepage, Produkt etc.) können nicht gelöscht werden. Du kannst aber eigene Tags erstellen und Faktoren frei zuordnen.
            </p>
            <p className="text-[13px] text-gray-500 leading-relaxed">
              Faktoren werden auf dem <strong>Faktoren-Tab</strong> per "Bearbeiten" einem Tag zugeordnet.
            </p>
          </div>
        </div>
      )}

      {/* ═══ MODAL: NEUER FAKTOR ═══ */}
      {showNewFactor && (
        <Modal onClose={() => setShowNewFactor(false)}>
          <div className="w-[520px]">
            <h2 className="text-[17px] font-bold mb-1">Neuer CRO-Faktor</h2>
            <p className="text-[13px] text-gray-500 mb-4">Eigenen Prüfungsfaktor anlegen</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Faktor-ID *</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal font-mono" placeholder="z.B. custom_trust_badge" value={newFactor.factor_id} onChange={(e) => setNewFactor({ ...newFactor, factor_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Name *</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" placeholder="z.B. Trust-Badge im Header" value={newFactor.name} onChange={(e) => setNewFactor({ ...newFactor, name: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Kategorie</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal bg-white" value={newFactor.category} onChange={(e) => setNewFactor({ ...newFactor, category: e.target.value })}>
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Impact</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal bg-white" value={newFactor.impact} onChange={(e) => setNewFactor({ ...newFactor, impact: e.target.value })}>
                  {Object.entries(IMPACTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1 font-medium">Check-Typ</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal bg-white" value={newFactor.check_type} onChange={(e) => setNewFactor({ ...newFactor, check_type: e.target.value })}>
                  <option value="ai">KI-Analyse</option>
                  <option value="rule">Regelbasiert</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1 font-medium">Bias / Psychologischer Effekt</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" placeholder="z.B. Social Proof, Authority Bias" value={newFactor.bias} onChange={(e) => setNewFactor({ ...newFactor, bias: e.target.value })} />
            </div>

            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1 font-medium">Prompt Hint (Anweisung für die KI) *</label>
              <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal h-[80px] resize-none" placeholder="Check if the page has..." value={newFactor.prompt_hint} onChange={(e) => setNewFactor({ ...newFactor, prompt_hint: e.target.value })} />
            </div>

            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowNewFactor(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">Abbrechen</button>
              <button onClick={handleCreateFactor} disabled={!newFactor.factor_id || !newFactor.name || !newFactor.prompt_hint || createFactor.isPending} className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                {createFactor.isPending ? 'Wird erstellt…' : 'Faktor anlegen'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ═══ MODAL: NEUER TAG ═══ */}
      {showNewTag && (
        <Modal onClose={() => setShowNewTag(false)}>
          <div className="w-[420px]">
            <h2 className="text-[17px] font-bold mb-1">Neuer Seitentyp-Tag</h2>
            <p className="text-[13px] text-gray-500 mb-4">Eigenen Seitentyp für Analysen anlegen</p>

            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1 font-medium">Name *</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" placeholder="z.B. SaaS-Landing, B2B-Formular" value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} />
            </div>

            <div className="mb-3">
              <label className="text-[12px] text-gray-500 block mb-1 font-medium">Beschreibung</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" placeholder="Wofür wird dieser Tag verwendet?" value={newTag.description} onChange={(e) => setNewTag({ ...newTag, description: e.target.value })} />
            </div>

            <div className="mb-4">
              <label className="text-[12px] text-gray-500 block mb-1 font-medium">Farbe</label>
              <div className="flex gap-2 items-center">
                <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                <input className="border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal w-[100px] font-mono" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} />
              </div>
            </div>

            <div className="flex gap-2.5 justify-end">
              <button onClick={() => setShowNewTag(false)} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">Abbrechen</button>
              <button onClick={handleCreateTag} disabled={!newTag.name || createTag.isPending} className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50">
                {createTag.isPending ? 'Wird erstellt…' : 'Tag anlegen'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
